import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, CreditCard, Star, Building2, MailOpen, LogOut, FileText, Inbox, Bell } from "lucide-react";
import { DashTabs, useTabParam } from "@/components/DashTabs";
import { AgreementsPanel } from "@/components/AgreementsPanel";
import { SecurityPanel } from "@/components/SecurityPanel";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { BillingHistory } from "@/components/BillingHistory";
import { AdSlot } from "@/components/AdSlot";
import { AppDownloadBanner } from "@/components/AppDownloadBanner";
import { ReferralCard } from "@/components/ReferralCard";
import { CollectorComplaints } from "@/components/CollectorComplaints";
import { LeadsBoard } from "@/components/collector/LeadsBoard";
import { NotifyPrefsCard } from "@/components/collector/NotifyPrefsCard";
import { CollectorProfileForm } from "@/components/collector/CollectorProfileForm";
import { ProfileCompletionCard } from "@/components/collector/ProfileCompletionCard";
import { CompanyLogo } from "@/components/TeamBadge";

const GOOGLE_REVIEW_URL = "https://g.page/r/CUqq1D1onNRqEBM/review";

const SubscribeBanner = () => (
  <div className="mb-8 rounded-2xl border border-[#CC5A3A]/30 bg-[#CC5A3A]/5 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="subscribe-banner">
    <div>
      <h2 className="font-heading text-lg text-[#1E3F33]">Subscribe to start receiving customers</h2>
      <p className="text-sm text-[#5C6B64] mt-1">Your profile won't be connected to customers until your $10.99/month subscription is active. You can still build your profile and upload certifications now.</p>
    </div>
    <Link to="/subscribe" data-testid="subscribe-banner-cta" className="btn-accent inline-flex items-center gap-2 shrink-0"><CreditCard className="w-4 h-4"/> Subscribe — $10.99/mo</Link>
  </div>
);

const CompanyBanner = ({ profile, onLeft }) => {
  const leave = async () => {
    if (!window.confirm(`Leave ${profile.company_name}? You'll become an independent collector and need your own $10.99/month subscription to keep receiving leads.`)) return;
    try { await api.post("/me/leave-company"); toast.success("You left the company"); await onLeft(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="mb-8 rounded-2xl border border-[#2D6A4F]/30 bg-[#2D6A4F]/5 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="company-member-banner">
      <div className="flex items-start gap-3">
        {profile.company_logo_url ? <CompanyLogo logo={profile.company_logo_url} name={profile.company_name} size="w-10 h-10" testid="company-member-logo" /> : <Building2 className="w-5 h-5 text-[#2D6A4F] mt-0.5 shrink-0"/>}
        <div>
          <h2 className="font-heading text-lg text-[#1E3F33]">Part of {profile.company_name}</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Your subscription is covered by your company{profile.subscription_active ? "" : " — leads start once your company's subscription is active"}. Your company can also view and respond to your leads.</p>
        </div>
      </div>
      <button type="button" onClick={leave} data-testid="leave-company-button" className="text-sm text-[#8A9A86] hover:text-[#E63946] inline-flex items-center gap-1.5"><LogOut className="w-4 h-4"/> Leave company</button>
    </div>
  );
};

const InviteBanners = ({ invites }) => invites.map((inv) => (
  <div key={inv.token} className="mb-6 rounded-2xl border border-[#E9C46A] bg-[#E9C46A]/10 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`company-invite-banner-${inv.token}`}>
    <div className="flex items-start gap-3">
      <MailOpen className="w-5 h-5 text-[#8A5A00] mt-0.5 shrink-0"/>
      <div>
        <h2 className="font-heading text-lg text-[#1E3F33]">{inv.company_name} invited you to join their team</h2>
        <p className="text-sm text-[#5C6B64] mt-1">Joining moves your subscription to the company account.</p>
      </div>
    </div>
    <Link to={`/invite/${inv.token}`} data-testid="company-invite-review-link" className="btn-primary text-sm">Review invite</Link>
  </div>
));

const GoogleReviewCard = () => (
  <div className="card-soft" data-testid="google-review-card">
    <h2 className="font-heading text-xl text-[#1E3F33] mb-2 flex items-center gap-2"><Star className="w-4 h-4 fill-[#CC5A3A] text-[#CC5A3A]"/> Enjoying the platform?</h2>
    <p className="text-sm text-[#5C6B64] mb-3">Leave us a quick review on Google — it helps more collectors discover us.</p>
    <button type="button" data-testid="google-review-button" onClick={() => window.open(GOOGLE_REVIEW_URL, "_blank", "noopener")} className="btn-secondary w-full inline-flex items-center justify-center gap-2">
      <Star className="w-4 h-4"/> Review us on Google
    </button>
  </div>
);

const Header = ({ profile, inCompany }) => (
  <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Collector dashboard</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">{profile.business_name}</h1>
    </div>
    <div className="flex items-center gap-3">
      {profile.subscription_active ? (
        <span className="badge-verified"><ShieldCheck className="w-3 h-3"/> Pro · Active</span>
      ) : !inCompany && (
        <Link to="/subscribe" data-testid="upgrade-cta" className="btn-accent inline-flex items-center gap-2"><CreditCard className="w-4 h-4"/> Upgrade to Pro</Link>
      )}
      {!inCompany && <Link to="/dashboard/subscription" data-testid="manage-subscription-link" className="text-sm text-[#5C6B64] hover:text-[#CC5A3A] underline-offset-2 hover:underline">Manage subscription</Link>}
    </div>
  </div>
);

export default function CollectorDashboard() {
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [invites, setInvites] = useState([]);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [tab, setTab] = useTabParam(["profile", "leads", "alerts"], needsSetup ? "profile" : "leads");

  const load = async () => {
    const [{ data: p }, { data: l }, { data: inv }] = await Promise.all([
      api.get("/collectors/me"),
      api.get("/leads/mine"),
      api.get("/me/company-invites").catch(() => ({ data: [] })),
    ]);
    setProfile(p);
    setNeedsSetup(!(p.certifications || []).length || (!!p.company_id && !p.profile_completion?.complete));
    setLeads(l);
    setInvites(inv);
  };

  useEffect(() => { load(); }, []);

  if (!profile) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;
  const inCompany = !!profile.company_id;
  const tabs = [
    { key: "profile", label: "Profile & Certifications", icon: FileText, count: (profile.certifications || []).length },
    { key: "leads", label: "Leads", icon: Inbox, count: leads.filter((l) => l.status === "requested").length, alert: true },
    { key: "alerts", label: "Alerts & Login", icon: Bell },
  ];

  return (
    <div className="container-x py-12" data-testid="collector-dashboard">
      <AppDownloadBanner />
      <Header profile={profile} inCompany={inCompany} />
      <InviteBanners invites={invites} />
      {inCompany ? <CompanyBanner profile={profile} onLeft={load} /> : !profile.subscription_active && <SubscribeBanner />}

      {inCompany && <ProfileCompletionCard completion={profile.profile_completion} who="you" nudges={profile.profile_nudges} onFix={tab !== "profile" ? () => setTab("profile") : null} />}
      <DashTabs tabs={tabs} value={tab} onChange={setTab} testid="collector-tabs" />

      {tab === "leads" && <LeadsBoard leads={leads} onChanged={load} showInsights />}

      {tab === "profile" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <CollectorProfileForm profile={profile} setProfile={setProfile} base="/collectors" reload={load} />
          </div>
          <div className="lg:col-span-4 space-y-6">
            {!inCompany && <SubscriptionCard />}
            {!inCompany && <BillingHistory />}
            <ReferralCard />
            <AdSlot placement="collector_dashboard" />
            <CollectorComplaints />
            <GoogleReviewCard />
          </div>
        </div>
      )}

      {tab === "alerts" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <NotifyPrefsCard prefs={profile.notify_prefs} email={profile.email} phone={profile.phone} endpoint="/collectors/notify-prefs" onSaved={(d) => setProfile((p) => ({ ...p, ...d }))} />
          </div>
          <div className="lg:col-span-5 space-y-6">
            <SecurityPanel />
            <AgreementsPanel />
          </div>
        </div>
      )}
    </div>
  );
}
